import { useState, useCallback } from 'react'
import { Plus } from 'lucide-react'
import { ButtonTabs } from '@/components/ui/ButtonTabs'
import { VocabularyTable } from './VocabularyTable'
import { VocabularyDetailSheet } from './VocabularyDetailSheet'
import { useVocabularyData } from '@/hooks/UseVocabularyData'
import { EDITOR_TAB_OPTIONS, type EditorTab } from './Types'
import type { VocabularyEntry } from '@/db/Types'

interface ModifiersTabProps {
	playbookId: string
	activeTab: EditorTab
	onTabChange: (tab: EditorTab) => void
}

export function ModifiersTab({
	playbookId,
	activeTab,
	onTabChange,
}: ModifiersTabProps) {
	const { vocabularyByCategory, isLoading, updateEntry, deleteEntry } = useVocabularyData(playbookId)
	const [selectedEntry, setSelectedEntry] = useState<VocabularyEntry | null>(null)
	const [sheetOpen, setSheetOpen] = useState(false)

	const modifierEntries = vocabularyByCategory.modifier ?? []

	const handleRowClick = useCallback((entry: VocabularyEntry) => {
		setSelectedEntry(entry)
		setSheetOpen(true)
	}, [])

	const handleUpdateCode = useCallback(async (id: string, newCode: string) => {
		await updateEntry(id, { code: newCode })
	}, [updateEntry])

	const handleDelete = useCallback(async (id: string) => {
		await deleteEntry(id)
		if (selectedEntry?.id === id) {
			setSheetOpen(false)
			setSelectedEntry(null)
		}
	}, [deleteEntry, selectedEntry])

	const handleEditEntity = useCallback((_entry: VocabularyEntry) => {
		// Modifiers don't have a dedicated editor page yet
	}, [])

	return (
		<>
			{/* Toolbar */}
			<div className="playbook-editor-tabs">
				<div className="playbook-editor-tabs-content">
					<ButtonTabs
						options={EDITOR_TAB_OPTIONS}
						value={activeTab}
						onChange={onTabChange}
						aria-label="Content type"
					/>

					<div className="playbook-editor-tabs-divider" />

					<div className="playbook-editor-toolbar-buttons">
						<button
							onClick={() => {/* TODO: new modifier creation */}}
							className="playbook-editor-action-button playbook-editor-action-button--primary"
						>
							<Plus className="icon-sm" />
							<span>New Modifier</span>
						</button>
					</div>
				</div>
			</div>

			{/* Content */}
			<div className="playbook-editor-content">
				<VocabularyTable
					entries={modifierEntries}
					groupByCategory={false}
					isLoading={isLoading}
					onRowClick={handleRowClick}
				/>
			</div>

			<VocabularyDetailSheet
				entry={selectedEntry}
				open={sheetOpen}
				onOpenChange={setSheetOpen}
				onUpdateCode={handleUpdateCode}
				onDelete={handleDelete}
				onEditEntity={handleEditEntity}
			/>
		</>
	)
}
